<template>
    <VJoyWrapper
        ref="similarProfileList"
        class="similar-profile-list"
        :class="{'similar-profile-list--no-horizontal-scroll': !horizontallyScrollable}"
        wrap="nowrap"
        direction="row"
        row-gap="2"
        column-gap="6"
        no-margin
        data-testid="similar-profiles-list"
        @wheel="onWheel"
    >
        <VJoyPanel v-for="(similarProfile, index) in similarProfiles" :key="`similar-${index}`" class="similar-profile-list__panel">
            <template #panel-body>
                <VJoyUserCard
                    class="similar-profile-list__card"
                    :full-name="`${similarProfile.firstname} ${similarProfile.lastname}`"
                    :sub-title="`${similarProfile.headline}`"
                    :link="similarProfileLink(similarProfile)"
                >
                    <template #user-card-avatar>
                        <VJoyAvatar :full-name="similarProfile.firstname" :photo-url="similarProfile.avatarUrl" size="large" />
                    </template>
                    <template #user-card-rating-stars>
                        <ReviewAndRecoIndicators
                            class="similar-profile-list__card-indicators"
                            :review-stats="similarProfile.reviews"
                            :number-of-recos="similarProfile.numberOfRecommendations ?? 0"
                        />
                    </template>
                </VJoyUserCard>
            </template>
        </VJoyPanel>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {onMounted, onUnmounted, useTemplateRef, type ComponentPublicInstance} from 'vue';
    import {VJoyAvatar, VJoyPanel, VJoyUserCard, VJoyWrapper} from '@maltjoy/core-vue';
    import type {SimilarProfile} from '@api';
    import {eventBus} from '~/config/events';
    import ReviewAndRecoIndicators from '~/features/similar-profiles/ui/ReviewAndRecoIndicator.vue';

    const props = withDefaults(
        defineProps<{
            similarProfiles: SimilarProfile[];
            horizontallyScrollable?: boolean;
        }>(),
        {horizontallyScrollable: true},
    );

    function similarProfileLink(similar: SimilarProfile): string {
        return similar.canonicalUrl.startsWith('/') ? similar.canonicalUrl : `/${similar.canonicalUrl}`;
    }

    function onWheel(event: WheelEvent) {
        if (!props.horizontallyScrollable) {
            return;
        }

        const el = event.currentTarget as HTMLElement;
        const atLeft = el.scrollLeft === 0 && event.deltaY < 0;
        const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth && event.deltaY > 0;

        if (atLeft || atRight) {
            return;
        }

        event.preventDefault();
        el.scrollLeft += event.deltaY;
    }

    /* Navigation buttons logic */
    const SCROLL_AMOUNT = 420; // should match the total missing sizes
    const similarProfileList = useTemplateRef<ComponentPublicInstance>('similarProfileList');
    function scrollSimilarProfile(itemDelta: number) {
        similarProfileList.value?.$el?.scrollBy({
            left: itemDelta * SCROLL_AMOUNT,
            behavior: 'smooth',
        });
    }
    onMounted(() => {
        if (!props.horizontallyScrollable) {
            return;
        }
        eventBus.on('similarProfiles:previous', () => scrollSimilarProfile(-1));
        eventBus.on('similarProfiles:next', () => scrollSimilarProfile(1));
    });

    onUnmounted(() => {
        if (!props.horizontallyScrollable) {
            return;
        }
        eventBus.off('similarProfiles:previous');
        eventBus.off('similarProfiles:next');
    });
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint' as joy;

    .similar-profile-list {
        overflow-x: auto;
        flex-wrap: wrap;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
        }

        &--no-horizontal-scroll {
            overflow-x: hidden;
        }

        @include joy.screen_xs(min) {
            flex-wrap: nowrap;
        }

        &__panel {
            padding: var(--joy-core-spacing-0);
            flex-grow: 1;

            @include joy.screen_xs(min) {
                flex-grow: 0;
            }
        }

        &__card {
            padding: var(--joy-core-spacing-3);
            max-height: 104px;
            max-width: 100%;

            @include joy.screen_xs(min) {
                min-width: 320px;
                max-width: 320px;
            }
        }

        &__card-indicators {
            padding-top: var(--joy-core-spacing-1);
        }

        :deep(.joy-user-card__title) {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-400);
            color: var(--joy-color-primary-50);
            word-break: normal;
            overflow: hidden;
            overflow-wrap: anywhere;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
        }

        :deep(.joy-user-card__subtitle) {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-400);
            word-break: normal;
            overflow: hidden;
            overflow-wrap: anywhere;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
        }
    }
</style>
